import React, { useEffect, useState } from 'react';
import Drawer from 'components/Drawer';

import globalMessages from 'translations/messages';
import { Form } from 'antd';
import useFormatMessage from 'hooks/useFormatMessage';
import Loading from 'components/Loading';
import useGlobalContext from 'hooks/useGlobalContext';
import { Prompt } from 'react-router';
import useUserConfirmation from 'hooks/useUserConfirmation';

const DrawerForm = ({
  open,
  onCancel,
  onDone,
  onSubmit,
  onRemove,
  doneLabel,
  cancelLabel,
  removeLabel,
  name,
  title,
  children,
  formReset,
  loading,
  width,
  footerStart,
  successMessage = globalMessages.successfullFormFeedback,
  errorMessage = globalMessages.errorFormFeedback,
  ...rest
}) => {
  const $t = useFormatMessage();
  const [form] = Form.useForm();
  const { message } = useGlobalContext();
  const [saving, setSaving] = useState(false);
  const [isTouched, setIsTouched] = useState(false);
  const getUserConfirmation = useUserConfirmation();
  const formValues = Form.useWatch(v => v, form);

  useEffect(() => {
    setIsTouched(false);
    form.resetFields();
  }, [formReset, open]);

  useEffect(() => {
    if (form.isFieldsTouched()) {
      setIsTouched(true);
    }
  }, [formValues]);

  const onFinish = async (values) => {
    try {
      setSaving(true);
      await onSubmit(values);
      if (successMessage) {
        message.success($t(successMessage));
      }
      if (onDone) {
        onDone();
      } else {
        onCancel();
      }
    } catch (e) {
      console.log(e);
      if (errorMessage) {
        message.error($t(errorMessage));
      }
    } finally {
      setSaving(false);
    }
  };

  const removeHandler = async () => {
    setIsTouched(false);
    await onRemove();
    onCancel();
  };

  const safeCancel = () => {
    if (isTouched) {
      getUserConfirmation($t(globalMessages.unsavedChanges), ok => { if (ok) onCancel(); });
    } else {
      onCancel();
    }
  };

  return (
    <Drawer
      name={name}
      title={title}
      open={open}
      onCancel={safeCancel}
      onOk={form.submit}
      confirmLoading={saving}
      onRemove={onRemove && removeHandler}
      okLabel={doneLabel}
      cancelLabel={cancelLabel}
      removeLabel={removeLabel}
      footerStart={footerStart}
      width={width}
      forceRender
      isTouched={isTouched}
    >
      <Form layout="vertical" form={form} onFinish={onFinish} onValuesChange={() => setIsTouched(true)} {...rest}>
        {open && (
          <>
            <Prompt when={isTouched} message={$t(globalMessages.unsavedChanges)} />
            {loading ? <Loading /> : children}
          </>
        )}
      </Form>
    </Drawer>
  );
};

export default DrawerForm;
